import { useCallback, useRef } from 'react';
import { stringPredicate } from 'js/utils/predicate.util';

import { getResponseData, parse, post } from '../helpers';
import { STREET_REGEX, POSTCODE_PROVIDER_ERROR } from '../constants';
import { useAddressLookupSwissConfig } from './useAddressLookupSwissConfig';

import * as Types from '../types';

export const useSearchStreetName = () => {
    const abortController = useRef<AbortController | null>(null);
    const prevSearch = useRef<string>('');
    const config = useAddressLookupSwissConfig();

    const prepareStreetNameRequest = useCallback((
        searchText: string,
        retrievedAddress?: Types.IParsedAutoCompleteItem | null
    ): Types.IAddressLookupRequest => {
        return {
            request: {
                HouseKey: 0,
                HouseNumber: '',
                HouseNumberAddition: '',
                Onrp: 0,
                StrId: 0,
                ZipAddition: '',
                Zipcode: retrievedAddress?.zip || '',
                Streetname: searchText,
                TownName: retrievedAddress?.city || ''
            },
            zipOrderMode: 0
        };
    }, []);

    return useCallback(async (
        searchText: string,
        retrievedAddress?: Types.IParsedAutoCompleteItem | null
    ): Promise<Types.IParsedAutoCompleteItem[]> => {
        prevSearch.current = searchText;
        abortController.current?.abort();
        abortController.current = new AbortController();

        if (searchText.length < 2 || !STREET_REGEX.test(searchText)) {
            throw {
                errorCode: POSTCODE_PROVIDER_ERROR.NOT_VALID_ERROR,
                errorMessage: 'playtech.mobile.system.form.addressLookupSwiss.internal.streetNameNotValid'
            };
        }

        try {
            const params = prepareStreetNameRequest(searchText, retrievedAddress);
            const response = await post(config.url, params, abortController.current?.signal);
            const data = await getResponseData(response);

            data.sort((row1, row2) => stringPredicate(row1.Streetname, row2.Streetname));

            return parse(data);
        } catch (error: any) {
            if (error?.errorCode === POSTCODE_PROVIDER_ERROR.ABORT_ERROR) {
                return [];
            }

            throw error;
        }
    }, [config, prepareStreetNameRequest]);
};
